/**
 * TeamSettings page - Manage team settings, members, and invitations
 */

import { ArrowLeft, Save, Loader2, UserPlus, Mail, X, RefreshCw, Users, Trash2, RotateCcw } from 'lucide-react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { CustomKpiLibrary } from '@/components/team/custom-kpis';
import { IntegrationsTab } from '@/components/team/IntegrationsTab';
import { TemplateList, TemplateEditor } from '@/components/template';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { useCustomKpis } from '@/hooks/useCustomKpis';
import { useDeletedProjects, useRestoreProject, type DeletedProject } from '@/hooks/useProjects';
import {
  useTeam,
  useUpdateTeam,
  useTeamMembers,
  usePendingInvites,
  useCreateInvite,
  useResendInvite,
  useCancelInvite,
} from '@/hooks/useTeams';
import { useTemplates, useCreateTemplate, useUpdateTemplate } from '@/hooks/useTemplates';
import { cn } from '@/lib/utils';
import type { TeamMember, TeamInvite, UpdateTeamRequest } from '@/types';
import type { CreateTemplateRequest, KpiOption, UpdateTemplateRequest } from '@/types/template';

// ============================================================================
// TYPES
// ============================================================================

type TabType = 'general' | 'members' | 'invites' | 'deleted' | 'integrations' | 'templates' | 'kpis';

// ============================================================================
// TAB NAVIGATION COMPONENT
// ============================================================================

interface TabButtonProps {
  label: string;
  value: TabType;
  active: boolean;
  onClick: () => void;
  badge?: number;
}

function TabButton({ label, active, onClick, badge }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'relative inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-all',
        active
          ? 'bg-brand/10 text-brand after:absolute after:-bottom-[9px] after:left-3 after:right-3 after:h-0.5 after:rounded-full after:bg-brand'
          : 'text-secondary hover:bg-surface-subtle hover:text-primary'
      )}
      aria-current={active ? 'page' : undefined}
    >
      {label}
      {badge !== undefined && badge > 0 && (
        <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-warning/10 text-warning">
          {badge}
        </span>
      )}
    </button>
  );
}

// ============================================================================
// MEMBER ROW COMPONENT
// ============================================================================

interface MemberRowProps {
  member: TeamMember;
}

function MemberRow({ member }: MemberRowProps) {
  const joinedDate = new Date(member.joinedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  // Get full name from firstName/lastName or fallback to legacy name
  const getFullName = (): string | null => {
    if (member.firstName && member.lastName) {
      return `${member.firstName} ${member.lastName}`;
    }
    if (member.firstName) {
      return member.firstName;
    }
    return member.name || null;
  };

  // Get initials for avatar (prefer firstName/lastName)
  const getInitials = (): string => {
    if (member.firstName && member.lastName) {
      return `${member.firstName[0]}${member.lastName[0]}`.toUpperCase();
    }
    if (member.firstName) {
      return member.firstName.substring(0, 2).toUpperCase();
    }
    if (member.name) {
      return member.name.charAt(0).toUpperCase();
    }
    return member.email.charAt(0).toUpperCase();
  };

  const fullName = getFullName();

  return (
    <div className="flex items-center justify-between py-3 border-b border-border-subtle last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-surface-subtle flex items-center justify-center">
          <span className="text-sm font-semibold text-secondary">
            {getInitials()}
          </span>
        </div>
        <div>
          <p className="text-sm font-medium text-primary">
            {fullName || 'No name set'}
          </p>
          <p className="text-xs text-secondary">{member.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span
          className={cn(
            'px-2 py-0.5 text-xs font-medium rounded-full',
            member.role === 'admin'
              ? 'bg-brand/10 text-brand'
              : 'bg-surface-subtle text-secondary'
          )}
        >
          {member.role}
        </span>
        <span className="text-xs text-muted">Joined {joinedDate}</span>
      </div>
    </div>
  );
}

// ============================================================================
// INVITE ROW COMPONENT
// ============================================================================

interface InviteRowProps {
  invite: TeamInvite;
  onResend: (email: string) => void;
  onCancel: (email: string) => void;
  isResending: boolean;
  isCanceling: boolean;
}

function InviteRow({ invite, onResend, onCancel, isResending, isCanceling }: InviteRowProps) {
  const expiresDate = new Date(invite.expiresAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="flex items-center justify-between py-3 border-b border-border-subtle last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
          <Mail className="h-4 w-4 text-warning" />
        </div>
        <div>
          <p className="text-sm font-medium text-primary">{invite.email}</p>
          <p className="text-xs text-secondary">
            Invited by {invite.invitedBy} {invite.daysAgo === 0 ? 'today' : `${invite.daysAgo}d ago`}
            {invite.resendCount > 0 && ` (resent ${invite.resendCount}x)`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted mr-2">Expires {expiresDate}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onResend(invite.email)}
          disabled={isResending}
          title="Resend invitation"
        >
          <RefreshCw className={cn('h-4 w-4', isResending && 'animate-spin')} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onCancel(invite.email)}
          disabled={isCanceling}
          title="Cancel invitation"
        >
          <X className="h-4 w-4 text-danger" />
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// DELETED PROJECT ROW COMPONENT
// ============================================================================

interface DeletedProjectRowProps {
  project: DeletedProject;
  onRestore: (projectId: string) => void;
  isRestoring: boolean;
}

function DeletedProjectRow({ project, onRestore, isRestoring }: DeletedProjectRowProps) {
  const deletedDate = project.deletedAt
    ? new Date(project.deletedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown';

  return (
    <div className="flex items-center justify-between py-3 border-b border-border-subtle last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-danger-soft flex items-center justify-center">
          <Trash2 className="h-4 w-4 text-danger" />
        </div>
        <div>
          <p className="text-sm font-medium text-primary">{project.name}</p>
          <p className="text-xs text-secondary">
            Deleted {deletedDate} · {project.daysRemaining} days remaining
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'px-2 py-0.5 text-xs font-medium rounded-full',
            project.daysRemaining <= 7
              ? 'bg-danger-soft text-danger'
              : 'bg-warning/10 text-warning'
          )}
        >
          {project.daysRemaining}d left
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onRestore(project.projectId)}
          disabled={isRestoring}
          leftIcon={<RotateCcw className={cn('h-4 w-4', isRestoring && 'animate-spin')} />}
        >
          Restore
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// INVITE MEMBER FORM
// ============================================================================

interface InviteMemberFormProps {
  teamId: string;
  onSuccess: () => void;
}

function InviteMemberForm({ teamId, onSuccess }: InviteMemberFormProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const toast = useToast();

  const createInvite = useCreateInvite(teamId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      const result = await createInvite.mutateAsync({ email: trimmedEmail });
      setEmail('');
      onSuccess();

      if (result.emailSent) {
        toast.success('Invitation sent', `An email has been sent to ${trimmedEmail}`);
      } else {
        toast.warning(
          'Invitation created',
          'Invite was created but email could not be sent. Share the invite link manually.'
        );
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        toast.error('Failed to invite', err.message);
      } else {
        setError('Failed to send invitation');
        toast.error('Failed to invite', 'An unexpected error occurred');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-3">
      <div className="flex-1">
        <Input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError('');
          }}
          placeholder="colleague@company.com"
          error={!!error}
          errorMessage={error}
          disabled={createInvite.isPending}
        />
      </div>
      <Button
        type="submit"
        leftIcon={<UserPlus className="h-4 w-4" />}
        loading={createInvite.isPending}
        disabled={!email.trim()}
      >
        Invite
      </Button>
    </form>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TeamSettings() {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const { data: team, isLoading, error } = useTeam(teamId!);
  const updateTeamMutation = useUpdateTeam(teamId!);
  const { data: members, isLoading: loadingMembers } = useTeamMembers(teamId!);
  const { data: invitesData, isLoading: loadingInvites } = usePendingInvites(teamId!);
  const resendInvite = useResendInvite(teamId!);
  const cancelInvite = useCancelInvite(teamId!);
  const { data: deletedProjects, isLoading: loadingDeleted } = useDeletedProjects(teamId!);
  const restoreProject = useRestoreProject();

  const [activeTab, setActiveTab] = useState<TabType>(
    (searchParams.get('tab') as TabType) || 'general'
  );

  const [teamName, setTeamName] = useState('');
  const [nameError, setNameError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [cancelingEmail, setCancelingEmail] = useState<string | null>(null);
  const [restoringProjectId, setRestoringProjectId] = useState<string | null>(null);

  // Template editing state
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    searchParams.get('tab') === 'templates' ? searchParams.get('edit') : null
  );
  const [isCreating, setIsCreating] = useState(false);
  // createDefaultMediaType removed - unified templates have no media type

  // Template hooks
  const { data: templatesData } = useTemplates(teamId);
  const { data: customKpis = [] } = useCustomKpis(teamId);
  const createTemplateMutation = useCreateTemplate(teamId);
  const updateTemplateMutation = useUpdateTemplate(teamId);

  const customKpiOptions = useMemo<KpiOption[]>(
    () =>
      customKpis
        .filter((definition) => definition.status === 'active')
        .map((definition) => ({
          id: definition.customKpiId,
          label: definition.name,
          shortLabel: definition.shortLabel,
          unit: definition.unit,
          category: 'derived',
          higherIsBetter: definition.higherIsBetter,
          description: definition.description || definition.formula,
        })),
    [customKpis],
  );

  useEffect(() => {
    if (team) {
      setTeamName(team.name);
    }
  }, [team]);

  useEffect(() => {
    const tabParam = searchParams.get('tab') as TabType;
    if (tabParam && ['general', 'members', 'invites', 'deleted', 'integrations', 'templates', 'kpis'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    // Deep link: ?tab=templates&edit={templateId}
    if (tabParam === 'templates') {
      const editParam = searchParams.get('edit');
      if (editParam) {
        setEditingTemplateId(editParam);
      }
    }
  }, [searchParams]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    // Clear template editing state when switching tabs
    if (tab !== 'templates') {
      setEditingTemplateId(null);
      setIsCreating(false);
      setSearchParams({ tab });
    } else {
      setSearchParams({ tab });
    }
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTeamName(e.target.value);
    setHasChanges(true);
    if (nameError) setNameError('');
  };

  const handleSave = async () => {
    const trimmedName = teamName.trim();
    if (!trimmedName) {
      setNameError('Team name is required');
      return;
    }
    if (trimmedName.length < 2) {
      setNameError('Team name must be at least 2 characters');
      return;
    }

    const updateData: UpdateTeamRequest = { name: trimmedName };

    try {
      await updateTeamMutation.mutateAsync(updateData);
      setHasChanges(false);
      toast.success('Team updated', 'Team settings have been saved');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again';
      toast.error('Failed to update team', message);
    }
  };

  const handleResendInvite = async (email: string) => {
    setResendingEmail(email);
    try {
      const result = await resendInvite.mutateAsync(email);
      if (result.emailSent) {
        toast.success('Invitation resent', `A new email has been sent to ${email}`);
      } else {
        toast.warning(
          'Invitation renewed',
          'Invite was renewed but email could not be sent. Share the invite link manually.'
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resend invitation';
      toast.error('Failed to resend', message);
    } finally {
      setResendingEmail(null);
    }
  };

  const handleCancelInvite = async (email: string) => {
    if (!confirm(`Cancel invitation for ${email}?`)) return;
    setCancelingEmail(email);
    try {
      await cancelInvite.mutateAsync(email);
      toast.success('Invitation cancelled', `The invitation for ${email} has been cancelled`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel invitation';
      toast.error('Failed to cancel', message);
    } finally {
      setCancelingEmail(null);
    }
  };

  const handleRestoreProject = async (projectId: string) => {
    setRestoringProjectId(projectId);
    try {
      await restoreProject.mutateAsync({ projectId, teamId: teamId! });
      toast.success('Project restored', 'The project has been restored to your active projects');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to restore project';
      toast.error('Failed to restore', message);
    } finally {
      setRestoringProjectId(null);
    }
  };

  const handleTemplateSave = useCallback(
    async (data: CreateTemplateRequest | UpdateTemplateRequest) => {
      try {
        if (editingTemplateId) {
          await updateTemplateMutation.mutateAsync({
            templateId: editingTemplateId,
            data: data as UpdateTemplateRequest,
          });
          toast.success('Template updated', 'Your template changes have been saved.');
        } else {
          await createTemplateMutation.mutateAsync(data as CreateTemplateRequest);
          toast.success('Template created', 'Your new template is ready to use.');
        }
        setEditingTemplateId(null);
        setIsCreating(false);
        setSearchParams({ tab: 'templates' });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Please try again';
        toast.error('Failed to save template', message);
      }
    },
    [editingTemplateId, updateTemplateMutation, createTemplateMutation, toast, setSearchParams]
  );

  const handleBack = () => {
    navigate('/projects');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 text-brand animate-spin" />
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <div className="text-center py-12">
            <h3 className="text-lg font-semibold text-danger mb-2">
              Failed to load team
            </h3>
            <p className="text-sm text-secondary mb-6">
              {error?.message || 'Team not found'}
            </p>
            <Button onClick={() => navigate('/projects')} variant="secondary">
              Back to Projects
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const pendingInvites = invitesData?.invites || [];
  const tabs: Array<{ value: TabType; label: string; badge?: number }> = [
    { value: 'general', label: 'General' },
    { value: 'members', label: 'Members', badge: members?.length },
    { value: 'invites', label: 'Invitations', badge: pendingInvites.length },
    { value: 'deleted', label: 'Deleted Projects', badge: deletedProjects?.length },
    { value: 'integrations', label: 'Integrations' },
    { value: 'templates', label: 'Templates', badge: templatesData?.templates?.length },
    { value: 'kpis', label: 'KPIs' },
  ];

  return (
    <div
      className={cn(
        'mx-auto w-full min-w-0 space-y-6 sm:space-y-8',
        activeTab === 'kpis' || activeTab === 'templates' ? 'max-w-7xl' : 'max-w-4xl'
      )}
    >
      {/* Page Header */}
      <div>
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Projects
        </button>

        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="text-3xl font-black text-primary leading-tight tracking-tight">
              {team.name}
            </h1>
            <p className="text-sm text-secondary mt-2">Team Settings</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border-subtle pb-2">
        <div className="sm:hidden">
          <label htmlFor="team-settings-section" className="sr-only">
            Team settings section
          </label>
          <select
            id="team-settings-section"
            value={activeTab}
            onChange={(event) => handleTabChange(event.target.value as TabType)}
            className="h-11 w-full rounded-md border border-border-subtle bg-surface px-3 text-sm font-semibold text-primary focus-visible:border-brand/55 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12"
          >
            {tabs.map((tab) => (
              <option key={tab.value} value={tab.value}>
                {tab.badge !== undefined && tab.badge > 0
                  ? `${tab.label} (${tab.badge})`
                  : tab.label}
              </option>
            ))}
          </select>
        </div>
        <div className="hidden gap-2 overflow-x-auto sm:flex">
          {tabs.map((tab) => (
            <TabButton
              key={tab.value}
              label={tab.label}
              value={tab.value}
              active={activeTab === tab.value}
              onClick={() => handleTabChange(tab.value)}
              badge={tab.badge}
            />
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <Card>
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-primary mb-1">
                General Settings
              </h2>
              <p className="text-sm text-secondary">
                Update your team&apos;s basic information
              </p>
            </div>

            <Input
              label="Team Name"
              value={teamName}
              onChange={handleNameChange}
              placeholder="e.g., Marketing Team"
              error={!!nameError}
              errorMessage={nameError}
              required
              disabled={updateTeamMutation.isPending}
            />

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-subtle">
              <Button
                onClick={handleSave}
                leftIcon={<Save className="h-4 w-4" />}
                loading={updateTeamMutation.isPending}
                disabled={!hasChanges || updateTeamMutation.isPending}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'members' && (
        <Card>
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-primary mb-1">
                  Team Members
                </h2>
                <p className="text-sm text-secondary">
                  {members?.length || 0} member{members?.length !== 1 ? 's' : ''} in this team
                </p>
              </div>
              <Button
                variant="secondary"
                leftIcon={<UserPlus className="h-4 w-4" />}
                onClick={() => handleTabChange('invites')}
              >
                Invite
              </Button>
            </div>

            {loadingMembers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 text-brand animate-spin" />
              </div>
            ) : members && members.length > 0 ? (
              <div className="divide-y divide-border-subtle">
                {members.map((member) => (
                  <MemberRow key={member.userId} member={member} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted mx-auto mb-3" />
                <p className="text-sm text-secondary">No team members yet</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'invites' && (
        <div className="space-y-6">
          <Card>
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-primary mb-1">
                  Invite a Team Member
                </h2>
                <p className="text-sm text-secondary">
                  Send an invitation email to add someone to your team
                </p>
              </div>

              <InviteMemberForm
                teamId={teamId!}
                onSuccess={() => {
                  // Refresh is handled by the mutation's onSuccess
                }}
              />
            </div>
          </Card>

          <Card>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-primary mb-1">
                    Pending Invitations
                  </h2>
                  <p className="text-sm text-secondary">
                    {pendingInvites.length} of {invitesData?.limit || 50} invitations used
                  </p>
                </div>
              </div>

              {loadingInvites ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 text-brand animate-spin" />
                </div>
              ) : pendingInvites.length > 0 ? (
                <div className="divide-y divide-border-subtle">
                  {pendingInvites.map((invite) => (
                    <InviteRow
                      key={invite.email}
                      invite={invite}
                      onResend={handleResendInvite}
                      onCancel={handleCancelInvite}
                      isResending={resendingEmail === invite.email}
                      isCanceling={cancelingEmail === invite.email}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Mail className="h-12 w-12 text-muted mx-auto mb-3" />
                  <p className="text-sm text-secondary">No pending invitations</p>
                  <p className="text-xs text-muted mt-1">
                    Invite team members using the form above
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'deleted' && (
        <Card>
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-primary mb-1">
                Deleted Projects
              </h2>
              <p className="text-sm text-secondary">
                Projects are permanently deleted 30 days after deletion. Restore them before then to recover.
              </p>
            </div>

            {loadingDeleted ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 text-brand animate-spin" />
              </div>
            ) : deletedProjects && deletedProjects.length > 0 ? (
              <div className="divide-y divide-border-subtle">
                {deletedProjects.map((project) => (
                  <DeletedProjectRow
                    key={project.projectId}
                    project={project}
                    onRestore={handleRestoreProject}
                    isRestoring={restoringProjectId === project.projectId}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Trash2 className="h-12 w-12 text-muted mx-auto mb-3" />
                <p className="text-sm text-secondary">No deleted projects</p>
                <p className="text-xs text-muted mt-1">
                  Deleted projects will appear here for 30 days before permanent deletion
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'integrations' && <IntegrationsTab teamId={teamId!} />}

      {activeTab === 'kpis' && (
        <CustomKpiLibrary teamId={teamId!} canManage={team.role === 'admin'} />
      )}

      {activeTab === 'templates' && (
        <>
          {editingTemplateId || isCreating ? (
            <TemplateEditor
              template={
                editingTemplateId
                  ? templatesData?.templates?.find(
                      (t) => t.templateId === editingTemplateId
                    )
                  : undefined
              }
              onSave={handleTemplateSave}
              onCancel={() => {
                setEditingTemplateId(null);
                setIsCreating(false);
                setSearchParams({ tab: 'templates' });
              }}
              isSaving={
                createTemplateMutation.isPending ||
                updateTemplateMutation.isPending
              }
              customKpiOptions={customKpiOptions}
            />
          ) : (
            <TemplateList
              teamId={teamId!}
              onEditTemplate={(id) => {
                setEditingTemplateId(id);
                setSearchParams({ tab: 'templates', edit: id });
              }}
              onCreateTemplate={() => {
                setIsCreating(true);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
